/**
 * Makana Product Analytics — lightweight event tracker
 * Sends events to Supabase Edge Function for cross-product analytics.
 * No PII: only numeric user_id, no RUT, no names, no emails.
 */

const INGEST_URL =
  'https://kcjslzqwjpdlhykmlybp.supabase.co/functions/v1/ingest-event';
const ANALYTICS_KEY =
  (typeof process !== 'undefined' && process.env?.REACT_APP_ANALYTICS_KEY) ||
  '';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER_SIZE = 50;
const STORAGE_KEY = 'mkn_analytics_queue';
const SESSION_KEY = 'mkn_analytics_session';

type EventCategory = 'navigation' | 'engagement' | 'conversion' | 'error';

interface AnalyticsEvent {
  event_name: string;
  event_category: EventCategory;
  source: string;
  session_id: string;
  user_id?: string;
  properties?: Record<string, unknown>;
  page_path?: string;
  device_type?: string;
  app_version?: string;
  business_id?: string;
}

let buffer: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let source = 'unknown';
let sessionId = '';
let userId: string | undefined;
let businessId: string | undefined;

function getSessionId(): string {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function loadOfflineQueue(): AnalyticsEvent[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      localStorage.removeItem(STORAGE_KEY);
      return JSON.parse(stored);
    }
  } catch {
    /* ignore */
  }
  return [];
}

function saveOfflineQueue(events: AnalyticsEvent[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    /* ignore, storage full */
  }
}

async function flush(): Promise<void> {
  if (buffer.length === 0) return;

  const events = [...buffer];
  buffer = [];

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (ANALYTICS_KEY) headers['x-analytics-key'] = ANALYTICS_KEY;
    const res = await fetch(INGEST_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      keepalive: true,
    });
    if (!res.ok) {
      saveOfflineQueue(events);
    }
  } catch {
    saveOfflineQueue(events);
  }
}

function enqueue(event: AnalyticsEvent): void {
  buffer.push(event);
  if (buffer.length >= MAX_BUFFER_SIZE) {
    flush();
  }
}

/** Initialize analytics. Call once on app startup. */
export function initAnalytics(appSource: string): void {
  source = appSource;
  sessionId = getSessionId();

  // Flush offline queue from previous session
  const offlineEvents = loadOfflineQueue();
  if (offlineEvents.length > 0) {
    buffer.push(...offlineEvents);
  }

  // Periodic flush
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  // Flush on page unload
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('beforeunload', () => flush());
}

/** Identify the current user (call after login). No PII. */
export function identifyUser(id: string, business?: string): void {
  userId = id;
  businessId = business;
}

/** Track a custom event. */
export function trackEvent(
  eventName: string,
  category: EventCategory = 'engagement',
  properties?: Record<string, unknown>
): void {
  enqueue({
    event_name: eventName,
    event_category: category,
    source,
    session_id: sessionId,
    user_id: userId,
    properties,
    page_path: window.location.pathname,
    device_type: getDeviceType(),
    business_id: businessId,
  });
}

/** Track a page view. */
export function trackPage(path?: string): void {
  trackEvent('page_view', 'navigation', {
    path: path || window.location.pathname,
    referrer: document.referrer || undefined,
  });
}
